const initialState = {
	loading: false,
	error: null,
	campaigns: { count: 0, results: [] },
	selectedCampaign: null,
	reports: { results: [] },
	availableDebriefReports: [],
	selectedReport: null,
	selectedEvent: null,
	eventResponses: null,
	events: { results: [] },
	actions: { results: [] },
	actionUpdates: { results: [] },
	analytics: null,
	dashboard: { overview: null, recent: null },
	manager: { reports: [], actions: [], events: [] },
	lastCreatedCampaign: null,
	reminderSchedule: [],
};

export default function ActionPlanningV3(state = initialState, action) {
	switch (action.type) {
		case "APV3_PENDING":
			return { ...state, loading: true, error: null };
		case "APV3_ERROR":
			return { ...state, loading: false, error: action.payload };
		case "APV3_CLEAR":
			return initialState;

		// Campaigns
		case "APV3_GET_CAMPAIGNS":
			return { ...state, loading: false, campaigns: action.payload };
		case "APV3_CREATE_CAMPAIGN":
			return {
				...state,
				loading: false,
				lastCreatedCampaign: action.payload,
				campaigns: {
					...state.campaigns,
					results: [action.payload, ...state.campaigns.results],
				},
			};
		case "APV3_FETCH_CAMPAIGN":
		case "APV3_GET_CAMPAIGN_SUMMARY":
			return { ...state, loading: false, selectedCampaign: action.payload };
		case "APV3_UPDATE_CAMPAIGN":
			return { ...state, loading: false, selectedCampaign: action.payload };
		case "APV3_CONFIGURE_EMAIL_TEMPLATE":
			return { ...state, loading: false };
		case "APV3_VALIDATE_EMAIL_TEMPLATES":
			return { ...state, loading: false };
		case "APV3_ADD_MANAGERS":
		case "APV3_ADD_GROUPS":
		case "APV3_ADVANCE_PHASE":
			return { ...state, loading: false, selectedCampaign: action.payload };
		case "APV3_ADD_SURVEY_MANAGERS":
			// Merge newly added managers into selected_managers immediately
			if (action.payload?.manager_objects && Array.isArray(action.payload.manager_objects) && state.selectedCampaign) {
				const existingManagers = Array.isArray(state.selectedCampaign.selected_managers) ? state.selectedCampaign.selected_managers : [];
				const existingIds = new Set(existingManagers.map(m => m?.id));
				const newManagers = action.payload.manager_objects.filter(m => m?.id && !existingIds.has(m.id));
				// Use API response as base, but merge in the new managers
				const apiSelectedManagers = Array.isArray(action.payload.selected_managers) ? action.payload.selected_managers : [];
				const apiIds = new Set(apiSelectedManagers.map(m => m?.id));
				// Only add managers that aren't already in the API response
				const managersToAdd = newManagers.filter(m => !apiIds.has(m.id));
				const updatedCampaign = {
					...action.payload,
					selected_managers: [...apiSelectedManagers, ...managersToAdd],
				};
				return { ...state, loading: false, selectedCampaign: updatedCampaign };
			}
			return { ...state, loading: false, selectedCampaign: action.payload };
		case "APV3_REMOVE_SURVEY_MANAGERS":
			// Remove managers from selected_managers immediately
			if (action.payload?.manager_ids && Array.isArray(action.payload.manager_ids) && state.selectedCampaign) {
				const removeIds = new Set(action.payload.manager_ids);
				// Use API response selected_managers if available, otherwise use existing state
				const sourceManagers = Array.isArray(action.payload.selected_managers)
					? action.payload.selected_managers
					: (Array.isArray(state.selectedCampaign.selected_managers) ? state.selectedCampaign.selected_managers : []);
				const updatedManagers = sourceManagers.filter(m => !removeIds.has(m?.id));
				const updatedCampaign = {
					...action.payload,
					selected_managers: updatedManagers,
				};
				return { ...state, loading: false, selectedCampaign: updatedCampaign };
			}
			return { ...state, loading: false, selectedCampaign: action.payload };

		// Reports
		case "APV3_GET_REPORTS":
			return { ...state, loading: false, reports: action.payload };
		case "APV3_START_REPORT_GENERATION":
			return { ...state, loading: false };
		case "APV3_GET_AVAILABLE_DEBRIEF_REPORTS":
			return { ...state, loading: false, availableDebriefReports: action.payload };
		case "APV3_FETCH_REPORT":
			return { ...state, loading: false, selectedReport: action.payload };
		case "APV3_APPROVE_REPORT":
		case "APV3_REJECT_REPORT":
		case "APV3_SEND_REPORT_NOW":
			return { ...state, loading: false, selectedReport: action.payload };

		// Events
		case "APV3_GET_EVENTS":
			return { ...state, loading: false, events: action.payload };
		case "APV3_CREATE_EVENT":
			return { ...state, loading: false, selectedEvent: action.payload, events: { ...state.events, results: [action.payload, ...(state.events?.results || [])] } };
		case "APV3_FETCH_EVENT":
			return { ...state, loading: false, selectedEvent: action.payload };
		case "APV3_UPDATE_EVENT":
			return { ...state, loading: false, selectedEvent: action.payload };
		case "APV3_LAUNCH_EVENT":
		case "APV3_CLOSE_EVENT":
			return { ...state, loading: false, selectedEvent: action.payload };
		case "APV3_GET_EVENT_RESPONSES":
			return { ...state, loading: false, eventResponses: action.payload };
		case "APV3_GET_REMINDER_SCHEDULE":
			return { ...state, loading: false, reminderSchedule: action.payload };
		case "APV3_UPSERT_REMINDER":
		case "APV3_DELETE_REMINDER":
			return { ...state, loading: false };

		// Actions
		case "APV3_GET_ACTIONS":
			return { ...state, loading: false, actions: action.payload };
		case "APV3_CREATE_ACTION":
			return {
				...state,
				loading: false,
				actions: {
					...state.actions,
					results: [action.payload, ...(state.actions?.results || [])],
				},
			};
		case "APV3_UPDATE_PROGRESS":
		case "APV3_ADD_ASSIGNEES":
			return { ...state, loading: false };
		case "APV3_GET_ACTION_UPDATES":
			return { ...state, loading: false, actionUpdates: action.payload };
		case "APV3_GET_AT_RISK_ACTIONS":
			return { ...state, loading: false, atRiskActions: action.payload };

		// Updates
		case "APV3_CREATE_UPDATE":
			return { ...state, loading: false };

		// Analytics
		case "APV3_GET_ANALYTICS":
		case "APV3_RECALCULATE_ANALYTICS":
			return { ...state, loading: false, analytics: action.payload };

		// Dashboard
		case "APV3_GET_DASHBOARD_OVERVIEW":
			return { ...state, loading: false, dashboard: { ...state.dashboard, overview: action.payload } };
		case "APV3_GET_RECENT_ACTIVITY":
			return { ...state, loading: false, dashboard: { ...state.dashboard, recent: action.payload } };

		// Manager
		case "APV3_GET_MANAGER_REPORTS":
			return { ...state, loading: false, manager: { ...state.manager, reports: action.payload } };
		case "APV3_GET_MANAGER_ACTIONS":
			return { ...state, loading: false, manager: { ...state.manager, actions: action.payload } };
		case "APV3_GET_MANAGER_EVENTS":
			return { ...state, loading: false, manager: { ...state.manager, events: action.payload } };

		default:
			return state;
	}
}


